import { Button, Form } from '@heroui/react'
import { use2FAManagement } from '@qse/auth-ui'
import { type ModalComponentProps, validateVerificationCode } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { HeroInput } from '@qse/ui'
import React from 'react'

export default function TwoFADisable(props: ModalComponentProps) {
  const { setModelActive, onClose } = props
  const { disable2FA, isLoading } = use2FAManagement()
  const { updateMfaStatus } = useUserStore()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const values = Object.fromEntries(formData)
    const code = values.code as string

    try {
      const success = await disable2FA(code)

      if (success) {
        updateMfaStatus('disabled')
        onClose()
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error)
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5 px-8 w-full"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-2 justify-center items-center text-current">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <p className="font-medium text-2xl">Disable Two-Factor Authentication</p>
          <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-400">
            This will remove the extra security layer from your account.
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  clipRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  fillRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Security Warning</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Disabling 2FA will make your account less secure. You can always re-enable it
                  later in your security settings.
                </p>
              </div>
            </div>
          </div>
        </div>

        <HeroInput
          isRequired
          control="text"
          description="Enter the 6-digit code from your authenticator app to confirm"
          label="Verification Code"
          maxLength={6}
          name="code"
          placeholder="Enter 6-digit code"
          radius="sm"
          size="md"
          validate={validateVerificationCode}
          variant="bordered"
        />

        <div className="flex justify-end gap-3 w-full">
          <Button disabled={isLoading} radius="sm" variant="flat" onPress={handleCancel}>
            Cancel
          </Button>
          <Button
            color="danger"
            disabled={isLoading}
            isLoading={isLoading}
            radius="sm"
            type="submit"
          >
            {isLoading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
